import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import moment from "moment";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import {
  FaBoxes,
  FaCheckCircle,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaHistory,
  FaMinus,
  FaPlus,
  FaRedo,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTimesCircle,
  FaWarehouse,
} from "react-icons/fa";
import "../product/AdminProducts.css";
import "./AdminInventory.css";

const DEFAULTS = { tab: "stock", q: "", status: "", category: "", sort: "stock_desc", page: 1, per_page: 25 };

const SORTS = [
  { value: "stock_desc", label: "Stock high–low" },
  { value: "stock_asc", label: "Stock low–high" },
  { value: "value_desc", label: "Value high–low" },
  { value: "price_desc", label: "Price high–low" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "newest", label: "Newest first" },
];

const REASONS = [
  "Restock from Supplier / Manufacturer",
  "Customer Return Restock",
  "Inventory Audit / Physical Count Correction",
  "Found Unrecorded Stock in Warehouse",
  "Damaged / Broken Goods Write-off",
  "Theft / Lost / Shrinkage Write-off",
  "Promotional Sample / Marketing Giveaway",
  "Internal Quality Control Quarantine",
  "Other Manual Adjustment",
];

const TX_TYPES = [
  { value: "all", label: "All events" },
  { value: "order_reserve", label: "Order reserves" },
  { value: "order_cancel_release", label: "Cancel releases" },
  { value: "refund_restock", label: "Refund restocks" },
  { value: "manual_adjustment", label: "Manual adjustments" },
];

function readFilters(search) {
  const params = new URLSearchParams(search);
  const filters = { ...DEFAULTS };
  Object.keys(DEFAULTS).forEach((key) => {
    if (!params.has(key)) return;
    filters[key] = key === "page" || key === "per_page" ? Math.max(1, Number(params.get(key)) || DEFAULTS[key]) : params.get(key);
  });
  return filters;
}

function toQuery(values, defaults) {
  const params = new URLSearchParams();
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (value === "" || value === null || value === undefined || value === defaults[key]) return;
    params.set(key, String(value));
  });
  return params.toString();
}

// The restock tab is the stock list limited to low and out of stock, lowest first.
function stockRequest(filters) {
  const restock = filters.tab === "restock";
  const params = new URLSearchParams({
    page: String(filters.page),
    per_page: String(filters.per_page),
    sort: restock ? "stock_asc" : filters.sort,
  });
  if (filters.q) params.set("q", filters.q);
  if (filters.category) params.set("category", filters.category);
  const status = restock ? "attention" : filters.status;
  if (status) params.set("status", status);
  return `/api/admin/inventory?${params.toString()}`;
}

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;
const compact = (value) => {
  const number = Number(value || 0);
  if (number >= 10000000) return `Rs. ${(number / 10000000).toFixed(2)} Cr`;
  if (number >= 100000) return `Rs. ${(number / 100000).toFixed(2)} Lac`;
  return money(number);
};

// A small cache of pages per request address; a page on its way is never asked for twice.
function usePagedFetch() {
  const cache = useRef(new Map());
  const inflight = useRef(new Map());
  const get = useCallback((url) => {
    if (cache.current.has(url)) return Promise.resolve(cache.current.get(url));
    if (inflight.current.has(url)) return inflight.current.get(url);
    const request = axios.get(url).then((res) => {
      if (res.data.status !== 200) throw new Error(res.data.message || "Could not load");
      cache.current.set(url, res.data);
      return res.data;
    }).finally(() => inflight.current.delete(url));
    inflight.current.set(url, request);
    return request;
  }, []);
  const clear = useCallback(() => cache.current.clear(), []);
  return useMemo(() => ({ get, clear }), [get, clear]);
}

export default function AdminInventory() {
  const history = useHistory();
  const location = useLocation();
  const [filters, setFilters] = useState(() => readFilters(location.search));
  const [searchText, setSearchText] = useState(filters.q);
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState("");
  const [exporting, setExporting] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const stock = usePagedFetch();

  const [txType, setTxType] = useState("all");
  const [txSearchText, setTxSearchText] = useState("");
  const [txSearch, setTxSearch] = useState("");
  const [txPage, setTxPage] = useState(1);
  const [txData, setTxData] = useState(null);
  const [txFetching, setTxFetching] = useState(false);
  const tx = usePagedFetch();

  const restock = filters.tab === "restock";
  const url = stockRequest(filters);

  useEffect(() => {
    document.title = "Inventory — Admin";
  }, []);

  useEffect(() => {
    const search = toQuery(filters, DEFAULTS);
    const next = search ? `?${search}` : "";
    if (location.search !== next) history.replace({ pathname: location.pathname, search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (searchText.trim() === filters.q) return undefined;
    const timer = setTimeout(() => setFilters((current) => ({ ...current, q: searchText.trim(), page: 1 })), 300);
    return () => clearTimeout(timer);
  }, [searchText, filters.q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTxSearch(txSearchText.trim());
      setTxPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [txSearchText]);

  // Stock list (also feeds the header numbers on every tab).
  useEffect(() => {
    let active = true;
    setFetching(true);
    setLoadError("");
    stock.get(url).then((result) => {
      if (!active) return;
      setData(result);
      setFetching(false);
      if (result.meta.page < result.meta.page_count) {
        stock.get(stockRequest({ ...filters, page: result.meta.page + 1 })).catch(() => {});
      }
    }).catch((error) => {
      if (!active) return;
      setFetching(false);
      setLoadError(error.response?.data?.message || "Inventory could not be loaded.");
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, reloadTick]);

  const txUrl = useMemo(() => {
    const params = new URLSearchParams({ page: String(txPage), per_page: "25" });
    if (txType !== "all") params.set("reference_type", txType);
    if (txSearch) params.set("search", txSearch);
    return `/api/admin/inventory/transactions?${params.toString()}`;
  }, [txPage, txType, txSearch]);

  useEffect(() => {
    if (filters.tab !== "history") return undefined;
    let active = true;
    setTxFetching(true);
    tx.get(txUrl).then((result) => {
      if (!active) return;
      setTxData(result);
      setTxFetching(false);
      if (result.meta && result.meta.page < result.meta.page_count) {
        tx.get(txUrl.replace(/page=\d+/, `page=${result.meta.page + 1}`)).catch(() => {});
      }
    }).catch(() => {
      if (active) setTxFetching(false);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txUrl, filters.tab, reloadTick]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, page: key === "page" ? value : 1 }));
  const goToPage = (page) => {
    setFilter("page", page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const switchTab = (tab) => setFilters((current) => ({ ...current, tab, page: 1 }));
  const resetFilters = () => {
    setSearchText("");
    setFilters((current) => ({ ...DEFAULTS, tab: current.tab, per_page: current.per_page }));
  };

  // After any stock change: drop cached pages and reload what is on screen.
  const reload = useCallback(() => {
    stock.clear();
    tx.clear();
    setReloadTick((tick) => tick + 1);
  }, [stock, tx]);

  const quickAdjust = useCallback((line, action, amount) => {
    const key = `${line.variant_id ? `v${line.variant_id}` : `p${line.id}`}`;
    setBusy(key);
    axios.post("/api/admin/inventory/quick-adjust", {
      product_id: line.id,
      variant_id: line.variant_id || undefined,
      action,
      amount,
    }).then((res) => {
      if (res.data.status === 200) {
        reload();
      } else {
        swal("Notice", res.data.message || "Failed to adjust stock", "warning");
      }
    }).catch((err) => {
      swal("Notice", err.response?.data?.message || "Could not change the stock.", "warning");
    }).finally(() => setBusy(""));
  }, [reload]);

  const openAdjust = useCallback((line, mode = "increment") => setAdjusting({ line, mode }), []);
  const toggleExpanded = useCallback((id) => setExpanded((current) => ({ ...current, [id]: !current[id] })), []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      let page = 1;
      let pageCount = 1;
      do {
        const res = await axios.get(stockRequest({ ...filters, page, per_page: 100 }));
        rows.push(...(res.data.inventory || []));
        pageCount = res.data.meta?.page_count || 1;
        page += 1;
      } while (page <= pageCount);
      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const lines = [["Product ID", "Product", "Option", "SKU", "Brand", "Category", "Price", "Stock", "Status", "Value"].join(",")];
      rows.forEach((product) => {
        lines.push([product.id, product.name, "", "", product.brand, product.category_name, product.selling_price, product.stock_qty, product.stock_status, product.total_valuation].map(escape).join(","));
        (product.variants || []).forEach((variant) => {
          lines.push([product.id, product.name, variant.title, variant.sku, product.brand, product.category_name, variant.price, variant.qty, variant.stock_status, variant.price * variant.qty].map(escape).join(","));
        });
      });
      const blob = new Blob([`﻿${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `inventory-${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      swal("Error", "The export could not be created.", "error");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const categories = data?.categories || [];
  const meta = data?.meta || { page: 1, page_count: 1, total: 0, per_page: filters.per_page };
  const rows = data?.inventory || [];
  const firstLoad = !data && fetching;
  const hasFilters = filters.q || filters.category || (!restock && filters.status);
  const attention = summary ? summary.low_stock + summary.out_of_stock : 0;
  const variantNote = summary ? [
    summary.variants_out ? `${summary.variants_out} ${summary.variants_out === 1 ? "option" : "options"} out` : "",
    summary.variants_low ? `${summary.variants_low} low` : "",
  ].filter(Boolean).join(" · ") : "";

  return (
    <div className="ap">
      <div className="ap-head">
        <div>
          <h4>Inventory {summary ? <span className="ap-count">{summary.total} products</span> : null}</h4>
          <p>Stock on hand for every product, size, and color, with quick restocks and a full movement log.</p>
        </div>
        <div className="ap-head-actions">
          <button type="button" className="ap-btn" onClick={reload} disabled={fetching}>
            <FaSyncAlt size={11} className={fetching && data ? "fa-spin" : ""} /> Refresh
          </button>
          <button type="button" className="ap-btn" onClick={exportCsv} disabled={exporting || !meta.total || filters.tab === "history"}>
            {exporting ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <FaDownload size={11} />}
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <Link to="/admin/products" className="ap-btn ap-btn-primary"><FaBoxes size={11} /> Products</Link>
        </div>
      </div>

      <div className="ap-stats">
        <Tile icon={<FaWarehouse />} tone="tone-blue" label="Units on hand" loading={firstLoad}
          value={summary ? summary.total_units.toLocaleString() : 0} />
        <Tile icon={<FaBoxes />} tone="tone-violet" label={summary ? `Cost ${compact(summary.total_cost_valuation)}` : "Retail value"} loading={firstLoad}
          value={summary ? compact(summary.total_retail_valuation) : 0} title="Retail value of stock on hand" />
        <Tile icon={<FaCheckCircle />} tone="tone-green" label="In stock" loading={firstLoad} value={summary?.in_stock || 0}
          active={!restock && filters.status === "in_stock"}
          onClick={() => setFilters((c) => ({ ...c, tab: "stock", status: c.status === "in_stock" ? "" : "in_stock", page: 1 }))} />
        <Tile icon={<FaExclamationTriangle />} tone="tone-amber" label="Low stock" loading={firstLoad} value={summary?.low_stock || 0} note={variantNote}
          active={!restock && filters.status === "low_stock"}
          onClick={() => setFilters((c) => ({ ...c, tab: "stock", status: c.status === "low_stock" ? "" : "low_stock", page: 1 }))} />
        <Tile icon={<FaTimesCircle />} tone="tone-red" label="Out of stock" loading={firstLoad} value={summary?.out_of_stock || 0}
          active={!restock && filters.status === "out_of_stock"}
          onClick={() => setFilters((c) => ({ ...c, tab: "stock", status: c.status === "out_of_stock" ? "" : "out_of_stock", page: 1 }))} />
      </div>

      <div className="ap-card">
        <div className="inv-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={filters.tab === "stock"} className={`inv-tab ${filters.tab === "stock" ? "is-active" : ""}`} onClick={() => switchTab("stock")}>
            <FaBoxes size={12} /> Stock on hand {summary ? <small>{summary.total}</small> : null}
          </button>
          <button type="button" role="tab" aria-selected={restock} className={`inv-tab ${restock ? "is-active" : ""}`} onClick={() => switchTab("restock")}>
            <FaExclamationTriangle size={12} /> Needs restock {summary ? <small className={attention ? "is-warn" : ""}>{attention}</small> : null}
          </button>
          <button type="button" role="tab" aria-selected={filters.tab === "history"} className={`inv-tab ${filters.tab === "history" ? "is-active" : ""}`} onClick={() => switchTab("history")}>
            <FaHistory size={12} /> Movement history {txData?.meta ? <small>{txData.meta.total}</small> : null}
          </button>
        </div>
      </div>

      {filters.tab !== "history" ? (
        <>
          <div className="ap-card ap-card-pad">
            <div className="inv-toolbar">
              <div className="ap-search">
                <FaSearch size={13} />
                <input className="form-control" placeholder="Search product, size, color, or SKU" value={searchText}
                  onChange={(event) => setSearchText(event.target.value)} aria-label="Search inventory" />
                {searchText ? <button type="button" className="ap-search-clear" onClick={() => setSearchText("")} aria-label="Clear search"><FaTimes size={11} /></button> : null}
              </div>
              {restock ? (
                <div className="ap-results m-0">Low and out of stock, lowest first</div>
              ) : (
                <select className="form-select" value={filters.status} onChange={(event) => setFilter("status", event.target.value)} aria-label="Stock status">
                  <option value="">Any stock</option>
                  <option value="in_stock">In stock</option>
                  <option value="low_stock">Low stock or options out</option>
                  <option value="out_of_stock">Out of stock</option>
                </select>
              )}
              <select className="form-select" value={filters.category} onChange={(event) => setFilter("category", event.target.value)} aria-label="Category">
                <option value="">All categories</option>
                {categories.map((category) => <option key={category.id} value={String(category.id)}>{category.name}</option>)}
              </select>
              {!restock ? (
                <select className="form-select" value={filters.sort} onChange={(event) => setFilter("sort", event.target.value)} aria-label="Sort">
                  {SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              ) : (
                <button type="button" className="ap-btn" onClick={resetFilters} disabled={!hasFilters}><FaRedo size={11} /> Reset</button>
              )}
            </div>
            <div className="ap-results">
              <span>
                {firstLoad ? "Loading inventory…" : <><strong>{meta.total}</strong> {meta.total === 1 ? "product" : "products"}{hasFilters ? (meta.total === 1 ? " matches" : " match") : ""}</>}
                {hasFilters && !restock ? <button type="button" className="inv-toggle ms-2" onClick={resetFilters}>Clear filters</button> : null}
              </span>
              {fetching && data ? <span className="d-inline-flex align-items-center gap-2"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Updating…</span> : null}
            </div>
          </div>

          <div className="ap-card" style={{ position: "relative" }}>
            {fetching && data ? <div className="ap-topbar" aria-hidden="true" /> : null}
            <div className="ap-table-wrap">
              <table className={`ap-table ${fetching && data ? "is-refreshing" : ""}`} aria-busy={fetching}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>On hand</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>{restock ? "Restock" : "Adjust"}</th>
                  </tr>
                </thead>
                <tbody>
                  {firstLoad ? (
                    Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} />)
                  ) : rows.length ? (
                    rows.map((product) => (
                      <StockRows
                        key={product.id}
                        product={product}
                        restock={restock}
                        open={restock || !!expanded[product.id]}
                        busy={busy}
                        onToggle={toggleExpanded}
                        onQuick={quickAdjust}
                        onAdjust={openAdjust}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="ap-empty">
                          <div className="ap-empty-icon">{loadError ? <FaExclamationTriangle /> : <FaCheckCircle />}</div>
                          <h6>{loadError ? "Inventory could not be loaded" : restock && !hasFilters ? "Everything is well stocked" : "No products match"}</h6>
                          <p className="mb-3">{loadError || (restock && !hasFilters ? "No product, size, or color is low or out of stock." : "Try a different search or clear the filters.")}</p>
                          {loadError ? <button type="button" className="ap-btn" onClick={reload}><FaRedo size={11} /> Try again</button>
                            : hasFilters ? <button type="button" className="ap-btn" onClick={resetFilters}><FaRedo size={11} /> Clear filters</button> : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data && meta.total > 0 ? (
              <Pager meta={meta} perPage={filters.per_page} onPerPage={(size) => setFilter("per_page", size)} onPage={goToPage} />
            ) : null}
          </div>
        </>
      ) : (
        <HistoryTab
          data={txData}
          fetching={txFetching}
          type={txType}
          onType={(value) => { setTxType(value); setTxPage(1); }}
          search={txSearchText}
          onSearch={setTxSearchText}
          onPage={(page) => { setTxPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        />
      )}

      {adjusting ? (
        <AdjustDialog
          line={adjusting.line}
          initialMode={adjusting.mode}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); reload(); }}
        />
      ) : null}
    </div>
  );
}

function Tile({ icon, tone, label, value, loading, active, onClick, note, title }) {
  const body = (
    <>
      <span className={`ap-stat-icon ${tone}`}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        {loading ? <span className="ap-skel" style={{ width: 60, height: 22, marginBottom: 4 }} /> : <div className="ap-stat-value">{value}</div>}
        <div className="ap-stat-label">{label}</div>
        {note && !loading ? <div className="ap-stat-note">{note}</div> : null}
      </div>
    </>
  );
  if (!onClick) return <div className="ap-stat" title={title}>{body}</div>;
  return <button type="button" className={`ap-stat ${active ? "is-active" : ""}`} onClick={onClick} aria-pressed={!!active}>{body}</button>;
}

function Pager({ meta, perPage, onPerPage, onPage }) {
  const from = (meta.page - 1) * meta.per_page + 1;
  const to = Math.min(meta.total, meta.page * meta.per_page);
  const numbers = Array.from({ length: meta.page_count }, (_, index) => index + 1)
    .filter((number) => number === 1 || number === meta.page_count || Math.abs(number - meta.page) <= 1);
  return (
    <div className="ap-pager">
      <div className="ap-pager-left">
        <span>Showing <strong>{from}–{to}</strong> of <strong>{meta.total}</strong></span>
        {onPerPage ? (
          <select className="form-select form-select-sm" value={perPage} onChange={(event) => onPerPage(Number(event.target.value))} aria-label="Rows per page">
            {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
          </select>
        ) : null}
      </div>
      {meta.page_count > 1 ? (
        <nav className="ap-pages" aria-label="Pages">
          <button type="button" className="ap-page" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)} aria-label="Previous page"><FaChevronLeft size={10} /></button>
          {numbers.map((number, index) => (
            <React.Fragment key={number}>
              {index > 0 && numbers[index - 1] !== number - 1 ? <span className="px-1">…</span> : null}
              <button type="button" className={`ap-page ${number === meta.page ? "is-current" : ""}`} onClick={() => onPage(number)} aria-current={number === meta.page ? "page" : undefined}>{number}</button>
            </React.Fragment>
          ))}
          <button type="button" className="ap-page" disabled={meta.page >= meta.page_count} onClick={() => onPage(meta.page + 1)} aria-label="Next page"><FaChevronRight size={10} /></button>
        </nav>
      ) : null}
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td>
        <div className="ap-product">
          <span className="ap-skel" style={{ width: 46, height: 46 }} />
          <div style={{ flex: 1 }}>
            <span className="ap-skel" style={{ width: "70%", height: 12, marginBottom: 6 }} />
            <span className="ap-skel" style={{ width: "40%", height: 10 }} />
          </div>
        </div>
      </td>
      {[80, 70, 60, 90, 160].map((width, index) => (
        <td key={index}><span className="ap-skel" style={{ width, height: 14, marginLeft: index === 4 ? "auto" : 0 }} /></td>
      ))}
    </tr>
  );
}

function StatusBadge({ status }) {
  if (status === "out_of_stock") return <span className="ap-badge ap-badge-red">Out of stock</span>;
  if (status === "low_stock") return <span className="ap-badge ap-badge-amber"><FaExclamationTriangle size={10} /> Low stock</span>;
  return <span className="ap-badge ap-badge-green"><FaCheckCircle size={10} /> In stock</span>;
}

function QtyCell({ qty }) {
  const color = qty <= 0 ? "#dc2626" : qty <= 5 ? "#d97706" : qty <= 10 ? "#2563eb" : "#16a34a";
  return (
    <div>
      <div className="inv-qty" style={{ color: qty <= 5 ? color : undefined }}>{qty.toLocaleString()}<small>units</small></div>
      <div className="inv-bar"><span style={{ width: `${Math.min(100, (qty / 30) * 100)}%`, background: color }} /></div>
    </div>
  );
}

function StepButtons({ line, restock, busy, onQuick, onAdjust }) {
  const key = line.variant_id ? `v${line.variant_id}` : `p${line.id}`;
  const working = busy === key;
  const locked = busy !== "";
  return (
    <div className="inv-step">
      {working ? <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" /> : null}
      {restock ? (
        <>
          <button type="button" className="ap-btn ap-btn-sm" disabled={locked} onClick={() => onQuick(line, "inc", 10)}>+10</button>
          <button type="button" className="ap-btn ap-btn-sm" disabled={locked} onClick={() => onQuick(line, "inc", 25)}>+25</button>
        </>
      ) : (
        <>
          <button type="button" className="ap-btn ap-btn-sm" disabled={locked || line.stock_qty <= 0} onClick={() => onQuick(line, "dec", 1)} aria-label="Remove 1">−1</button>
          <button type="button" className="ap-btn ap-btn-sm" disabled={locked} onClick={() => onQuick(line, "inc", 1)} aria-label="Add 1">+1</button>
          <button type="button" className="ap-btn ap-btn-sm" disabled={locked} onClick={() => onQuick(line, "inc", 5)} aria-label="Add 5">+5</button>
        </>
      )}
      <button type="button" className="ap-btn ap-btn-sm ap-btn-dark" disabled={locked} onClick={() => onAdjust(line, "increment")}>
        <FaEdit size={10} /> {restock ? "Custom" : "Adjust"}
      </button>
    </div>
  );
}

const StockRows = React.memo(function StockRows({ product, restock, open, busy, onToggle, onQuick, onAdjust }) {
  const variants = product.variants || [];
  const needy = variants.filter((variant) => variant.stock_status !== "in_stock");
  const shown = restock ? needy : variants;
  const out = variants.filter((variant) => variant.qty <= 0).length;
  const low = needy.length - out;

  return (
    <>
      <tr>
        <td>
          <div className="ap-product">
            <img className="ap-thumb" src={getProductImageUrl(product.image)} alt="" loading="lazy" decoding="async" width="46" height="46" onError={handleProductImageError} />
            <div style={{ minWidth: 0 }}>
              <Link to={`/admin/edit-product/${product.id}`} className="ap-product-name" title={product.name}>{product.name}</Link>
              <div className="ap-product-meta"><span className="ap-id">#{product.id}</span>{product.brand ? ` · ${product.brand}` : ""}</div>
              {variants.length > 0 && !restock ? (
                <button type="button" className="inv-toggle" onClick={() => onToggle(product.id)} aria-expanded={open}>
                  {open ? <FaChevronUp size={9} /> : <FaChevronDown size={9} />}
                  {variants.length} {variants.length === 1 ? "option" : "options"}
                  {needy.length ? ` · ${[out ? `${out} out` : "", low ? `${low} low` : ""].filter(Boolean).join(", ")}` : ""}
                </button>
              ) : null}
            </div>
          </div>
        </td>
        <td><span className="ap-badge ap-badge-muted">{product.category_name}</span></td>
        <td>
          <div className="ap-price">{money(product.selling_price)}</div>
          <div className="ap-cost">Value {money(product.total_valuation)}</div>
        </td>
        <td><QtyCell qty={Number(product.stock_qty || 0)} /></td>
        <td>
          <StatusBadge status={product.stock_status} />
          {variants.length && needy.length && product.stock_status !== "out_of_stock" ? (
            <div className="ap-variant-note">{needy.length} of {variants.length} options need stock</div>
          ) : null}
        </td>
        <td>
          {variants.length ? (
            <div className="inv-step">
              <Link to={`/admin/edit-product/${product.id}`} className="ap-btn ap-btn-sm"><FaEdit size={10} /> Edit</Link>
            </div>
          ) : (
            <StepButtons line={product} restock={restock} busy={busy} onQuick={onQuick} onAdjust={onAdjust} />
          )}
        </td>
      </tr>
      {open && shown.map((variant) => {
        const line = {
          ...product,
          name: `${product.name} — ${variant.title}`,
          stock_qty: Number(variant.qty),
          selling_price: variant.price,
          variant_id: variant.id,
          variants: [],
        };
        return (
          <tr className="inv-variant" key={`${product.id}-${variant.id}`}>
            <td>
              <div className="inv-variant-title">{variant.title}</div>
              <div className="inv-sku">{variant.sku ? `SKU ${variant.sku}` : "No SKU"}</div>
            </td>
            <td />
            <td><span className="ap-price" style={{ fontWeight: 600 }}>{money(variant.price)}</span></td>
            <td><QtyCell qty={Number(variant.qty || 0)} /></td>
            <td><StatusBadge status={variant.stock_status} /></td>
            <td><StepButtons line={line} restock={restock} busy={busy} onQuick={onQuick} onAdjust={onAdjust} /></td>
          </tr>
        );
      })}
    </>
  );
});

function HistoryTab({ data, fetching, type, onType, search, onSearch, onPage }) {
  const rows = data?.transactions || [];
  const firstLoad = !data && fetching;
  return (
    <>
      <div className="ap-card ap-card-pad">
        <div className="inv-toolbar" style={{ gridTemplateColumns: "minmax(220px, 2fr) minmax(160px, 1fr)" }}>
          <div className="ap-search">
            <FaSearch size={13} />
            <input className="form-control" placeholder="Search by product or note" value={search} onChange={(event) => onSearch(event.target.value)} aria-label="Search history" />
            {search ? <button type="button" className="ap-search-clear" onClick={() => onSearch("")} aria-label="Clear search"><FaTimes size={11} /></button> : null}
          </div>
          <select className="form-select" value={type} onChange={(event) => onType(event.target.value)} aria-label="Event type">
            {TX_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </div>
      <div className="ap-card" style={{ position: "relative" }}>
        {fetching && data ? <div className="ap-topbar" aria-hidden="true" /> : null}
        <div className="ap-table-wrap">
          <table className={`ap-table ${fetching && data ? "is-refreshing" : ""}`}>
            <thead>
              <tr>
                <th>When</th>
                <th>Product</th>
                <th>Event</th>
                <th>Before</th>
                <th>Change</th>
                <th>After</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {firstLoad ? Array.from({ length: 6 }, (_, index) => (
                <tr key={index}>{[90, 200, 110, 40, 50, 40, 160].map((width, cell) => <td key={cell}><span className="ap-skel" style={{ width, height: 14 }} /></td>)}</tr>
              )) : rows.length ? rows.map((tx) => {
                const change = Number(tx.qty_change);
                return (
                  <tr key={tx.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{moment(tx.created_at).format("DD MMM YYYY")}</div>
                      <div className="ap-product-meta">{moment(tx.created_at).format("hh:mm A")} · {tx.formatted_date}</div>
                    </td>
                    <td>
                      <div className="ap-product">
                        <img className="ap-thumb" style={{ width: 34, height: 34 }} src={getProductImageUrl(tx.product_image)} alt="" loading="lazy" onError={handleProductImageError} />
                        <div style={{ minWidth: 0 }}>
                          <div className="ap-product-name" style={{ maxWidth: 240 }} title={tx.product_name}>{tx.product_name}</div>
                          <div className="ap-product-meta ap-id">#{tx.product_id} · Tx {tx.id}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={`inv-type t-${tx.reference_type_badge || "info"}`}>{tx.reference_type_label}</span></td>
                    <td className="ap-id">{tx.qty_before}</td>
                    <td><span className={`inv-change ${change > 0 ? "is-up" : change < 0 ? "is-down" : "is-zero"}`}>{change > 0 ? `+${change}` : change}</span></td>
                    <td className="ap-id" style={{ fontWeight: 700 }}>{tx.qty_after}</td>
                    <td style={{ maxWidth: 260, fontSize: 12.5 }}>
                      {tx.notes || "—"}
                      {tx.reference_id ? <div className="ap-product-meta">Ref #{tx.reference_id}</div> : null}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="7">
                    <div className="ap-empty">
                      <div className="ap-empty-icon"><FaHistory /></div>
                      <h6>No stock movements</h6>
                      <p className="mb-0">Orders, refunds, and adjustments show up here.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {data?.meta && data.meta.total > 0 ? <Pager meta={data.meta} onPage={onPage} /> : null}
      </div>
    </>
  );
}

function AdjustDialog({ line, initialMode, onClose, onSaved }) {
  const current = Number(line.stock_qty || 0);
  const [mode, setMode] = useState(initialMode);
  const [qty, setQty] = useState(initialMode === "set" ? current : 5);
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const min = mode === "set" ? 0 : 1;
  const result = mode === "increment" ? current + qty : mode === "decrement" ? Math.max(0, current - qty) : Math.max(0, qty);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changeMode = (next) => {
    setMode(next);
    if (next === "set") setQty(current);
    else if (qty < 1) setQty(1);
    setReason(next === "increment" ? REASONS[0] : next === "decrement" ? REASONS[4] : REASONS[2]);
  };

  const submit = (event) => {
    event.preventDefault();
    if (qty < min) return;
    setSaving(true);
    axios.post("/api/admin/inventory/adjust", {
      product_id: line.id,
      variant_id: line.variant_id || undefined,
      type: mode,
      quantity: Number(qty),
      reason,
      notes,
    }).then((res) => {
      if (res.data.status === 200) {
        swal("Saved", `Stock is now ${res.data.data?.qty_after ?? result} units.`, "success", { buttons: false, timer: 1200 });
        onSaved();
      } else {
        swal("Error", res.data.message || "Failed to adjust inventory", "error");
      }
    }).catch((err) => {
      swal("Error", err.response?.data?.message || "Could not save the adjustment.", "error");
    }).finally(() => setSaving(false));
  };

  return (
    <div className="inv-modal-backdrop" role="presentation" onClick={onClose}>
      <form className="inv-modal" role="dialog" aria-modal="true" aria-label="Adjust stock" onClick={(event) => event.stopPropagation()} onSubmit={submit}>
        <div className="inv-modal-head">
          <div>
            <h5>Adjust stock</h5>
            <p>#{line.id} · {line.name}</p>
          </div>
          <button type="button" className="ap-icon-btn" onClick={onClose} aria-label="Close"><FaTimes size={12} /></button>
        </div>
        <div className="inv-modal-body">
          <div className="inv-modes" role="radiogroup" aria-label="Adjustment type">
            <button type="button" className={`inv-mode m-add ${mode === "increment" ? "is-active" : ""}`} onClick={() => changeMode("increment")}><FaPlus size={10} /> Add</button>
            <button type="button" className={`inv-mode m-remove ${mode === "decrement" ? "is-active" : ""}`} onClick={() => changeMode("decrement")}><FaMinus size={10} /> Remove</button>
            <button type="button" className={`inv-mode m-set ${mode === "set" ? "is-active" : ""}`} onClick={() => changeMode("set")}>= Set to</button>
          </div>

          <div className="inv-calc">
            <div><span>Now</span><strong>{current}</strong></div>
            <div className="text-muted">{mode === "increment" ? "+" : mode === "decrement" ? "−" : "→"}</div>
            <div><span>{mode === "set" ? "New count" : "Change"}</span><strong>{qty}</strong></div>
            <div className="text-muted">=</div>
            <div><span>After</span><strong style={{ color: "var(--ap-accent)" }}>{result}</strong></div>
          </div>

          <label className="form-label fw-semibold small mb-1" htmlFor="inv-qty">{mode === "set" ? "New units on hand" : "Units"}</label>
          <div className="input-group">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setQty(Math.max(min, qty - 1))} aria-label="Less"><FaMinus size={10} /></button>
            <input id="inv-qty" type="number" min={min} className="form-control text-center fw-bold" value={qty}
              onChange={(event) => setQty(Math.max(min, parseInt(event.target.value, 10) || 0))} />
            <button type="button" className="btn btn-outline-secondary" onClick={() => setQty(qty + 1)} aria-label="More"><FaPlus size={10} /></button>
          </div>
          <div className="inv-quick">
            {[1, 5, 10, 25, 50, 100].map((amount) => (
              <button key={amount} type="button" className="ap-btn ap-btn-sm" onClick={() => setQty(amount)}>{amount}</button>
            ))}
          </div>

          <label className="form-label fw-semibold small mb-1 mt-3" htmlFor="inv-reason">Reason</label>
          <select id="inv-reason" className="form-select" value={reason} onChange={(event) => setReason(event.target.value)} required>
            {REASONS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>

          <label className="form-label fw-semibold small mb-1 mt-3" htmlFor="inv-notes">Notes <span className="text-muted fw-normal">(optional)</span></label>
          <textarea id="inv-notes" className="form-control" rows="2" placeholder="e.g. PO #1042 received from supplier" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </div>
        <div className="inv-modal-foot">
          <button type="button" className="ap-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ap-btn ap-btn-primary" disabled={saving || qty < min}>
            {saving ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <FaCheckCircle size={11} />}
            {saving ? "Saving…" : "Save adjustment"}
          </button>
        </div>
      </form>
    </div>
  );
}
